import { processApiFieldErrors } from '../../../utils/processApiFieldErrors'
import { getErrorMessage } from '../../../utils/utils'
import {
  FAMILY_BE_TO_FORM_FIELD,
  FAMILY_BE_TO_FIELD_TITLE,
} from './familyContactErrorMaps'

export interface ApplyFamilyApiErrorDeps {
  /** Set the inline per-field error map (keyed by form field: nameFirst/nameLast/email). */
  setFieldErrors: (errors: Record<string, string>) => void
  /** Summary toast (web: message.error; mobile: an Alert.alert wrapper). */
  notifyError: (message: string) => void
}

/**
 * Translate a thrown family-contact API error into the form's inline field-error
 * map — the family counterpart of applyPlayerApiError (these forms are
 * useState-controlled, not Formik, so we write to a plain setter). Field-error
 * responses show only the inline per-field messages (no summary toast). Falls
 * back to a toast (backend .message, then getErrorMessage) when the error
 * carries no mapped invalid_fields.
 */
export const applyFamilyApiError = (
  err: unknown,
  deps: ApplyFamilyApiErrorDeps
): void => {
  const { setFieldErrors, notifyError } = deps
  const e = err as {
    invalid_fields?: Record<string, string | string[]>
    message?: string
  }

  const { formErrors, titles, hasFieldErrors } = processApiFieldErrors(
    e.invalid_fields,
    {
      fieldMap: FAMILY_BE_TO_FORM_FIELD,
      titleMap: FAMILY_BE_TO_FIELD_TITLE,
    }
  )

  if (!hasFieldErrors || titles.length === 0) {
    // No field errors, or every key was unmapped — clear inline errors and
    // surface the backend message (or a generic fallback).
    setFieldErrors({})
    notifyError(e.message ?? getErrorMessage(err))
    return
  }

  // Field errors: show them inline only, no summary toast.
  setFieldErrors(formErrors)
}
